// src/pages/History/ResultHistory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

// ── Helpers ───────────────────────────────────────────────────────────────────
const toISTTime = (utcValue) => {
  if (!utcValue) return '--:--';
  const d = new Date(utcValue);
  if (isNaN(d)) return '--:--';
  return d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getSuitIcon = (suit) => {
  switch (suit?.toLowerCase()) {
    case 'clubs':    return '/img/Clubs.png';
    case 'diamonds': return '/img/Diamonds.png';
    case 'hearts':   return '/img/Hearts.png';
    case 'spades':   return '/img/Spades.png';
    default:         return '';
  }
};

// Roulette number colour (matches standard European wheel)
const RED_NUMBERS = new Set([2,4,6,8,10,13,15,17,19,20,22,24,26,28,31,33,35,37]);
const getRouletteColor = (num, apiColor) => {
  if (apiColor) return apiColor;
  if (num === 0) return 'green';
  return RED_NUMBERS.has(Number(num)) ? 'red' : 'black';
};

const ROULETTE_CIRCLE_STYLE = (color) => {
  const map = { red: '#dc3545', black: '#212529', green: '#198754' };
  return { backgroundColor: map[color] || '#6c757d', color: '#fff' };
};

// ── Sub-components ────────────────────────────────────────────────────────────

const EmptyState = () => (
  <div className="text-center w-100 py-4 text-muted">
    No results found for the selected criteria
  </div>
);

// Classic (RoyalKing) result boxes
const ClassicResults = ({ results }) => {
  if (!results.length) return <EmptyState />;
  return (
    <div className="result-row">
      {results.map((r, i) => (
        <div key={r._id || i} className="result-box">
          <div className="result-circle d-flex align-items-center justify-content-center gap-1">
            <span className="fw-bold">{r.card || '-'}</span>
            {r.suit && (
              <img src={getSuitIcon(r.suit)} alt={r.suit} style={{ width: 22, height: 22 }} />
            )}
          </div>
          <div className="result-time">{toISTTime(r.time)}</div>
          <div className="result-round-id small text-muted">#{r.drawNumber}</div>
        </div>
      ))}
    </div>
  );
};

// Roulette result boxes — coloured circles
const RouletteResults = ({ results }) => {
  if (!results.length) return <EmptyState />;
  return (
    <div className="result-row">
      {results.map((r, i) => {
        const color = getRouletteColor(r.result, r.color);
        return (
          <div key={r._id || i} className="result-box">
            <div
              className="result-circle d-flex align-items-center justify-content-center"
              style={ROULETTE_CIRCLE_STYLE(color)}
            >
              <span className="fw-bold">{r.result ?? '-'}</span>
            </div>
            <div className="result-time">{toISTTime(r.time)}</div>
            <div className="result-round-id small text-muted">#{r.drawNumber}</div>
          </div>
        );
      })}
    </div>
  );
};

// FunTarget result boxes
const FunTargetResults = ({ results }) => {
  if (!results.length) return <EmptyState />;
  return (
    <div className="result-row">
      {results.map((r, i) => (
        <div key={r._id || i} className="result-box">
          <div className="result-circle d-flex align-items-center justify-content-center">
            <span className="fw-bold">{r.result ?? '-'}</span>
          </div>
          <div className="result-time">{toISTTime(r.time)}</div>
          <div className="result-round-id small text-muted">#{r.drawNumber}</div>
        </div>
      ))}
    </div>
  );
};

// Section wrapper with heading + count badge
const GameSection = ({ title, count, children }) => (
  <div className="mb-4">
    <div className="d-flex align-items-center gap-2 mb-2">
      <h4 className="admin-card-heading-two mb-0">{title}</h4>
      {count > 0 && (
        <span className="badge bg-secondary">{count}</span>
      )}
    </div>
    {children}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const ResultHistory = () => {
  const [loading, setLoading]   = useState(false);
  const [allResults, setAllResults] = useState([]);
  const [filters, setFilters]   = useState({ gameType: '', date: '' });
  const [pagination, setPagination] = useState({
    page: 1, limit: 50, total: 0, pages: 0, hasMore: false,
  });

  const fetchResults = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (filters.gameType) params.gameType = filters.gameType;
      if (filters.date)     params.date     = filters.date;

      const res = await api.get('/admin/results/history', { params });
      const data = res.data;

      setAllResults(data.results || []);
      setPagination(prev => ({
        ...prev,
        page,
        total:   data.pagination?.total   ?? 0,
        pages:   data.pagination?.pages   ?? 1,
        hasMore: data.pagination?.hasMore ?? false,
      }));
    } catch (err) {
      console.error('Error fetching results:', err);
      toast.error('Failed to fetch result history');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Fetch on filter change (reset to page 1)
  useEffect(() => { fetchResults(1); }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Split results by game type
  const classic   = allResults.filter(r => r.gameType === 'classic');
  const roulette  = allResults.filter(r => r.gameType === 'roulette');
  const funtarget = allResults.filter(r => r.gameType === 'funtarget');

  // Which sections to show based on filter
  const showClassic   = !filters.gameType || filters.gameType === 'classic';
  const showRoulette  = !filters.gameType || filters.gameType === 'roulette';
  const showFunTarget = !filters.gameType || filters.gameType === 'funtarget';

  return (
    <div className="result-history-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Result History</h1>
        </div>

        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Last Result History</h2>
          </div>
          <div className="admin-card-body">

            {/* ── Filters ── */}
            <div className="row g-3 pb-3 align-items-end">
              <div className="col-lg-3 col-md-6">
                <label className="admin-label-one">Select Game</label>
                <select
                  name="gameType"
                  value={filters.gameType}
                  onChange={handleFilterChange}
                  className="form-select admin-select-one"
                >
                  <option value="">All Games</option>
                  <option value="classic">RoyalKing</option>
                  <option value="roulette">Roulette</option>
                  <option value="funtarget">Fun Target</option>
                </select>
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="admin-label-one">Select Date</label>
                <input
                  type="date"
                  name="date"
                  value={filters.date}
                  onChange={handleFilterChange}
                  className="form-control admin-input-one"
                />
              </div>
              <div className="col-lg-3">
                <button
                  className="new-user-add"
                  onClick={() => fetchResults(1)}
                  disabled={loading}
                >
                  {loading ? 'Loading…' : 'Generate Report'}
                </button>
              </div>
            </div>

            {/* ── Results ── */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            ) : (
              <>
                {showClassic && (
                  <GameSection title="RoyalKing Results" count={classic.length}>
                    <ClassicResults results={classic} />
                  </GameSection>
                )}

                {showRoulette && (
                  <GameSection title="Roulette Results" count={roulette.length}>
                    <RouletteResults results={roulette} />
                  </GameSection>
                )}

                {showFunTarget && (
                  <GameSection title="Fun Target Results" count={funtarget.length}>
                    <FunTargetResults results={funtarget} />
                  </GameSection>
                )}

                {/* Summary + Load More */}
                {pagination.total > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      Showing {allResults.length} of {pagination.total} results
                    </small>
                    {pagination.hasMore && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => fetchResults(pagination.page + 1)}
                        disabled={loading}
                      >
                        Load More
                      </button>
                    )}
                  </div>
                )}

                {allResults.length === 0 && !loading && (
                  <div className="text-center py-4 text-muted">
                    No results found for the selected criteria
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultHistory;